import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api"; // Your API service
import { getPharmacyId } from "../../utils/commonFunctions"; // Utility function
import {
  GET_BOOKING_STATS_URL,
  GET_EMPLOYEE_DASHBOARD_DATA,
  GET_PHARMACY_LEADERBOARD_URL,
} from "../../services/urls";

export const LEADERBOARD_PERIODS = {
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_WEEK: "this_week",
  LAST_WEEK: "last_week",
};

// Initial state for the slice
const initialState = {
  loading: false,
  statsLoading: false,
  leaderboardLoading: false,
  leaderboard: null,
  leaderboardError: null,
  stats: {
    totalBooking: 0,
    totalUnattendedBooking: 0,
    totalCancelledBooking: 0,
    totalOpenBooking: 0,
    totalFinishedBooking: 0,
    totalRevenue: 0,
    missedRevenue: 0, // Added missedRevenue field
    timeSeriesData: [], // To store time series data for bookings & revenue
  },
  pieChartData: {
    labels: ["Finished", "Open", "Cancelled", "Unattended"], // Added "Missed Revenue"
    data: [0, 0, 0, 0], // Data based on the stats
  },
  labTestBookings: [], // Holds lab test booking data
  topLabTests: [], // Holds top 3 lab tests
  error: null,
  employeeDashboardData: [],
  employeeDashboardLoading: false,
};

// Async thunk to fetch booking stats and time series data
export const fetchEmployeeDashboardList = createAsyncThunk(
  "stats/fetchEmployeeDashboardList",
  async ({ date }, { rejectWithValue }) => {
    try {
      const normalizeToDateOnly = (value) => {
        if (!value) return null;
        if (typeof value === "string") {
          // If it's already a string in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
          }
          // Otherwise, convert to date and extract date part
          return new Date(value).toISOString().split("T")[0];
        }
        // If it's a Date object, extract date part
        return new Date(value).toISOString().split("T")[0];
      };

      const dateParam = normalizeToDateOnly(date) ?? new Date().toISOString().split("T")[0];
      const pharmacyId = getPharmacyId();
      if (!pharmacyId) {
        return rejectWithValue("Pharmacy ID not found");
      }

      const response = await api.get(
        GET_EMPLOYEE_DASHBOARD_DATA.replace(":pharmacyId", pharmacyId),
        {
          params: {
            date: dateParam,
          },
        }
      );
      return response.data?.data?.bookings?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking stats"
      );
    }
  }
);

// Async thunk to fetch booking stats and time series data
export const fetchBookingStats = createAsyncThunk(
  "stats/fetchBookingStats",
  async (selectedDates, { rejectWithValue }) => {
    try {
      const defaultStartDate = new Date().getFullYear() + "-01-01";
      const defaultEndDate = new Date().getFullYear() + "-12-31";

      const normalizeToDateOnly = (value) => {
        if (!value) return null;
        if (typeof value === "string") {
          // If it's already a string in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
          }
          // Otherwise, convert to date and extract date part
          return new Date(value).toISOString().split("T")[0];
        }
        // If it's a Date object, extract date part
        return new Date(value).toISOString().split("T")[0];
      };

      const startDateParam = normalizeToDateOnly(selectedDates?.startDate) ?? defaultStartDate;
      const endDateParam = normalizeToDateOnly(selectedDates?.endDate) ?? defaultEndDate;

      const pharmacyId = getPharmacyId();
      if (!pharmacyId) {
        return rejectWithValue("Pharmacy ID not found");
      }

      const response = await api.get(
        GET_BOOKING_STATS_URL.replace(":pharmacyId", pharmacyId),
        {
          params: {
            startDate: startDateParam,
            endDate: endDateParam,
          },
        }
      ); // Replace with the correct API route
      const stats = response.data?.data;

      // Calculate pie chart data
      const pieChartData = {
        labels: ["Finished", "Open", "Cancelled", "Unattended"], // Updated labels to include "Missed Revenue"
        data: [
          stats.totalFinishedBooking,
          stats.totalOpenBooking,
          stats.totalCancelledBooking,
          stats.totalUnattendedBooking,
        ],
      };

      // Return the updated stats data, pie chart data, and lab test data
      return {
        stats,
        pieChartData,
        labTestBookings: stats.labTestBookings,
        topLabTests: stats.topLabTests,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking stats"
      );
    }
  }
);

export const fetchPharmacyLeaderboard = createAsyncThunk(
  "stats/fetchPharmacyLeaderboard",
  async (period, { rejectWithValue }) => {
    try {
      const pharmacyId = getPharmacyId();
      if (!pharmacyId) {
        return rejectWithValue("Pharmacy ID not found");
      }
      const response = await api.get(
        GET_PHARMACY_LEADERBOARD_URL.replace(":pharmacyId", pharmacyId),
        {
          params: { period },
        }
      );
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy leaderboard"
      );
    }
  }
);

const statsSlice = createSlice({
  name: "stats",
  initialState,
  reducers: {
    updateBookingInEmployeeDashboard: (state, action) => {
      const { bookingId, updatedData } = action.payload;
      if (state.employeeDashboardData && Array.isArray(state.employeeDashboardData)) {
        const index = state.employeeDashboardData.findIndex(
          (booking) => booking._id === bookingId
        );
        if (index !== -1) {
          const currentBooking = state.employeeDashboardData[index];
          const firstName = updatedData.firstName !== undefined ? updatedData.firstName : (currentBooking.firstName || currentBooking.fullName?.split(' ')[0] || '');
          const lastName = updatedData.lastName !== undefined ? updatedData.lastName : (currentBooking.lastName || currentBooking.fullName?.split(' ').slice(1).join(' ') || '');
          state.employeeDashboardData[index] = {
            ...currentBooking,
            ...updatedData,
            firstName,
            lastName,
            fullName: `${firstName} ${lastName}`.trim(),
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookingStats.pending, (state) => {
        state.loading = true;
        state.statsLoading = true;
      })
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.loading = false;
        state.statsLoading = false;
        state.error = null;
        // Set the fetched stats, pie chart data, and lab test data in state
        state.stats = action.payload.stats;
        state.pieChartData = action.payload.pieChartData;
        state.labTestBookings = action.payload.labTestBookings;
        state.topLabTests = action.payload.topLabTests;
      })
      .addCase(fetchBookingStats.rejected, (state, action) => {
        state.loading = false;
        state.statsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchEmployeeDashboardList.pending, (state) => {
        state.employeeDashboardLoading = true;
      })
      .addCase(fetchEmployeeDashboardList.fulfilled, (state, action) => {
        state.employeeDashboardLoading = false;
        state.error = null;
        state.employeeDashboardData = action.payload;
      })
      .addCase(fetchEmployeeDashboardList.rejected, (state, action) => {
        state.employeeDashboardLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPharmacyLeaderboard.pending, (state) => {
        state.leaderboardLoading = true;
        state.leaderboardError = null;
      })
      .addCase(fetchPharmacyLeaderboard.fulfilled, (state, action) => {
        state.leaderboardLoading = false;
        state.leaderboard = action.payload;
      })
      .addCase(fetchPharmacyLeaderboard.rejected, (state, action) => {
        state.leaderboardLoading = false;
        state.leaderboardError = action.payload;
      });
  },
});

export const { updateBookingInEmployeeDashboard } = statsSlice.actions;
export default statsSlice.reducer;
